import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, PlusGate } from "@/components/Common";

const fmt = (d) => new Date(d + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });

export default function Reflections({ embedded }) {
  const { user } = useAuth();
  const plus = user.plan === "plus";
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/reflections").then(({ data }) => setList(data)); }, []);

  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/reflections/generate"); setList((l) => [data, ...l]); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div>
      {embedded ? <><h2 className="font-display text-xl">Weekly reviews</h2><p className="text-xs text-stone-500 mb-4 mt-1">What went well, one pattern, and one focus for next week. Best on Sundays.</p></> : <PageTitle eyebrow="Weekly reflection" title="Your week, in a few lines" sub="What went well, one pattern worth knowing, and one gentle focus for next week." />}
      {plus ? (
        <button onClick={generate} disabled={busy} data-testid="reflection-generate-btn" className="btn-terra px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-60 mb-8">
          <Sparkles className="h-4 w-4" />{busy ? "Reflecting on your week…" : "Write this week's reflection"}
        </button>
      ) : (
        <div className="mb-8"><PlusGate testid="reflection-plus-gate" title="Weekly reflections are part of Plus" body="Each week CAMRYN reads your days and writes a short, honest summary with one focus for the week ahead." /></div>
      )}
      <div className="space-y-4">
        {list.map((r, i) => (
          <article key={r.id} className={`card p-6 rise d${Math.min(i + 1, 5)}`} data-testid={`reflection-card-${i}`}>
            <div className="eyebrow mb-2">{fmt(r.week_start)} – {fmt(r.week_end)} · {r.days_logged} check-ins</div>
            <p className="font-display text-xl leading-snug text-stone-900">{r.summary}</p>
            <dl className="mt-5 grid gap-4 sm:grid-cols-3 text-sm">
              <div><dt className="font-semibold text-[#6E8B74] mb-1">Went well</dt><dd className="text-stone-700">{r.went_well}</dd></div>
              <div><dt className="font-semibold text-[#D99B52] mb-1">Pattern</dt><dd className="text-stone-700">{r.pattern}</dd></div>
              <div><dt className="font-semibold text-[#C86D51] mb-1">Next week's focus</dt><dd className="text-stone-700">{r.focus}</dd></div>
            </dl>
          </article>
        ))}
        {list.length === 0 && plus && <p className="text-sm text-stone-500" data-testid="reflections-empty">No reflections yet. You need at least 2 check-ins this week.</p>}
      </div>
    </div>
  );
}
